import { View } from 'backbone';
import _ from 'underscore';
import ButtonHtml from '../html/genericButton.html';
import Analytics from './analytics';
import Arcade from './engine/arcade/arcade';
import { getLogger } from './logging/logger';
import AudioManager from './manager/audioManager';
import { SOUNDS } from './manager/audioManager.types';
import gsap, { BUTTON_EFFECTS } from './util/buttonGSAPEffects';

/**
 *
 * Note: Touch event support was removed from this component.
 * If touch event functionality is needed, refer to commit 756cf033f3484776e587c4cb91c9917e8a982cfe
 * for the previous implementation.
 *
 */

/**
 * @typedef {object} GenericButtonOptions
 * @property {string} [id] Unique identifier of button
 * @property {TAG_NAMES} [type] A type for the button
 * @property {string[]} [text] Array of strings to be used as lines
 * @property {string} [className] Space seperated class names to be applied to the button
 * @property {string} [image] - Url to the image to be used
 * @property {string} [tagName] - The HTML tag type to use on the element
 * @property {string} [dataId] - A data attribute with the name 'id' to be added to the HTML element
 * @property {string} [route] - Used for anchor based buttons to navigate to a specific page in the application
 * @property {string} [uri] - Used for anchor based buttons to navigate to a specific URL
 * @property {string} [name] - A line of text to be displayed on the button
 * @property {string} [title] - A line of text to be displayed on the button
 * @property {string} [subtitle] - A line of text to be displayed on the button
 * @property {SOUNDS} [audioClick] - A line of text to be displayed on the button
 * @property {SOUNDS} [audioFocus] - A line of text to be displayed on the button
 * @property {string[]} [rewards] - rewardIds to be added to analytic event when clicked
 * @property {string} [href] -
 * */

/** @enum {typeof ATTRIBUTES[keyof typeof ATTRIBUTES]} */
const ATTRIBUTES = Object.freeze({
	id: 'id',
	type: 'type',
	text: 'text',
	className: 'className',
	image: 'image',
	tagName: 'tagName',
	dataId: 'dataId',
	route: 'route',
	uri: 'uri',
	name: 'name',
	title: 'title',
	subtitle: 'subtitle',
});

/** @enum {typeof TAG_NAMES[keyof typeof TAG_NAMES]} */
const TAG_NAMES = Object.freeze({
	anchor: 'a',
	button: 'button',
});

/** @enum {typeof EVENTS[keyof typeof EVENTS]} */
const EVENTS = Object.freeze({
	click: 'click',
	focus: 'focusin mouseover',
	unfocus: 'focusout mouseout',
	down: 'keydown mousedown',
	up: 'keyup mouseup',
	incorrect: 'incorrect',
});

const DEFAULT_CLASSES = 'btn selectable';

export { ATTRIBUTES, BUTTON_EFFECTS, EVENTS };

export default class GenericButton extends View {
	get logger() {
		const { id, name, dataId = '' } = this;
		return getLogger(`GenericButton][${id || name || dataId}`);
	}

	className() {
		return DEFAULT_CLASSES;
	}

	events() {
		return {
			click: this.onClick,

			focusin: this.onFocus,
			mouseover: this.onFocus,

			focusout: this.onUnfocus,
			mouseout: this.onUnfocus,

			keydown: this.onPressDown,
			mousedown: this.onPressDown,

			keyup: this.onRelease,
			mouseup: this.onRelease,

			incorrect: this.onIncorrect,
		};
	}

	get template() {
		return _.template(ButtonHtml);
	}

	/**
	 * Checks the button data for a route or URI to determine if an HTML Anchor or Button tag should be used.
	 *
	 * @param {GenericButtonOptions} options Data used to populate the button
	 */
	preinitialize(options) {
		this.logger.log(`[${this.preinitialize.name}]`, { options });

		// Determine tagName based on route and uri from options
		const { tagName = '', route = '', uri = '' } = options;
		if (!tagName) {
			this.tagName = route || uri ? TAG_NAMES.anchor : TAG_NAMES.button;
		}
	}

	/**
	 *
	 * @param {GenericButtonOptions} options
	 */
	constructor(options) {
		super(options);
	}

	/**
	 * @param {GenericButtonOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, { options });

		// **************** DATA FIELDS **************** //
		/** @type {string} Unique identifier of button */
		this.id = undefined;
		/** @type {TAG_NAMES} The type of button */
		this.type = 'button';
		/** @type {string} Generic field for engines to tap into */
		this.dataId = undefined;
		/** @type {string[]} List of rewards to be submitted for click */
		this.rewards = [];

		// **************** NAVIGATIONAL FIELDS **************** //
		/** @type {string} Route that maps to internal page */
		this.route = undefined;
		/** @type {string} For deeplinking */
		this.uri = undefined;

		// **************** VISUAL FIELDS **************** //
		/** @type {string[]} An array of string(s) for multi-line support */
		this.text = [];
		/** @type {string} Line of text. // ! Deprecated Soon. */
		this.name = undefined;
		/** @type {string} Line of text. // ! Deprecated Soon. */
		this.title = undefined;
		/** @type {string} Line of text. // ! Deprecated Soon. */
		this.subtitle = undefined;
		/** @type {string} Path/Url of an image to embed */
		this.image = undefined;

		// **************** AUDIO FIELDS **************** //
		/** @type {SOUNDS} Audio file to be played when button is clicked */
		this.soundClick = SOUNDS.buttonSelect;
		/** @type {SOUNDS} Audio file to be played when button is focused */
		this.soundFocus = SOUNDS.buttonChange;

		// **************** RUNTIME ATTRIBTUES **************** //
		/** @type {string} */
		this.href = undefined;
		/** @type {Boolean} Whether or not instance is in the "down" state of a click */
		this.isPressedDown = false;
		/** @type {GSAPTimeline} */
		this.timeline = gsap.timeline();
		/** @type {string} This dictates which enter to listen to (keyboard-enter) */
		this.typeOfPressDown = undefined;

		Object.assign(this, options);

		if (this.dataId !== undefined) {
			this.$el.attr('data-id', this.dataId);
		}
		this.$el.addClass(DEFAULT_CLASSES);

		this.render(options);
	}

	render(btnData) {
		this.logger.log(`[${this.render.name}]`, { btnData });

		const templateData = this.getButtonData(btnData);
		const { href, type } = templateData;

		// If href is set, this is an anchor tag.
		if (href) {
			this.$el.attr({ href });
		} else {
			// Otherwise, this is a true <button>.
			this.$el.attr({ type });
		}
		this.$el.append(this.template(templateData));

		return this;
	}

	onPressDown(event) {
		this.logger.log(`[${this.onPressDown.name}]`, { event, isDown: this.isPressedDown });

		if (this.isPressedDown || !this.isValidPressDown(event)) {
			return;
		}

		this.isPressedDown = true;
		this.timeline[BUTTON_EFFECTS.down](this.el).then(() => this.$el.addClass('pressedDown'));
	}

	isValidPressDown(event) {
		const { type, code, which: pointerCode } = event;
		this.logger.log(`[${this.isValidPressDown.name}]`, { type, code, pointerCode, event });

		if (this.typeOfPressDown) {
			// Press has already been initiated
			return false;
		}

		const isKeyboardEnter = type === 'keydown' && code === 'Enter';
		const isMouseDown = type === 'mousedown' && pointerCode === 1; // checks mouse event and checks for left button

		if (isKeyboardEnter || isMouseDown) {
			this.typeOfPressDown = type;
			return true;
		}

		return false;
	}

	/**
	 * Handle click
	 * @param {Event} event DOM event
	 */
	onClick(event) {
		this.logger.debug(`[${this.onClick.name}]`, { event, isPressedDown: this.isPressedDown });

		if (this.isPressedDown) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}
	}

	async onRelease(event) {
		this.logger.debug(`[${this.onRelease.name}]`, { event, isPressedDown: this.isPressedDown });

		if (!this.isPressedDown || !this.isValidRelease(event)) {
			event.preventDefault();
			return;
		}

		// Visually being released. Remove class immediately.
		this.$el.removeClass('pressedDown');

		this.isPressedDown = false;
		this.timeline[BUTTON_EFFECTS.up](this.el).then(() => {
			this.typeOfPressDown = undefined;
		});

		if (this.soundClick) {
			AudioManager.playSound(this.soundClick);
		}

		if (this.rewards?.length) {
			// We wait for the return to give time for any rewards to be queued up
			await Analytics.sendEvent('ButtonClick', {
				btnId: this.id || this.dataId,
				rewards: this.rewards,
			});
		}

		/**
		 * 'down', 'click', and 'up' type events happen in a different order depending on
		 * if they were triggered by a mouse or keyboard event. We need to trigger 'click'
		 * for keyboard input here.
		 */
		if (event.type !== 'mouseup') {
			this.el.click();
		}
	}

	isValidRelease(event) {
		const { type, code, which: pointerCode } = event;
		this.logger.debug(`[${this.isValidRelease.name}]`, { type, code, pointerCode, event });

		if (!this.typeOfPressDown) {
			// No press down was initiated
			return false;
		}

		const isKeyboardEnter = type === 'keyup' && code === 'Enter';
		const isMouseUp = type === 'mouseup' && pointerCode === 1; // checks mouse event and checks for left button

		const matchingRelease =
			(isKeyboardEnter && this.typeOfPressDown === 'keydown') ||
			(isMouseUp && this.typeOfPressDown === 'mousedown');

		if (matchingRelease) {
			this.typeOfPressDown = undefined;
			return true;
		}

		return false;
	}

	onFocus(event) {
		this.logger.debug(`[${this.onFocus.name}]`, { event });

		if (this.soundFocus) {
			AudioManager.playSound(this.soundFocus);
		}

		this.timeline[BUTTON_EFFECTS.focus](this.el);
	}

	onUnfocus(event) {
		this.logger.debug(`[${this.onUnfocus.name}]`, { event });

		if (this.isPressedDown) {
			this.timeline[BUTTON_EFFECTS.up](this.el);
			this.isPressedDown = false;
			this.typeOfPressDown = undefined;
		}

		this.timeline.add(gsap.effects[BUTTON_EFFECTS.focus](this.el).reverse());
	}

	onIncorrect(event) {
		this.logger.log(`[${this.onIncorrect.name}]`, { event });

		this.timeline[BUTTON_EFFECTS.incorrect](this.el);
	}

	/**
	 * Receives arbitrary data and returns the config needed for genericButton.html
	 * Assumes caller has provided it's necessary button properties.
	 *
	 * @param {GenericButtonOptions} btnData contains generic data for a button
	 * @returns {GenericButtonOptions} with needed properties to drop into template
	 */
	getButtonData(btnData) {
		this.logger.log(`[${this.getButtonData.name}]`, { btnData });

		if (!btnData) {
			this.logger.warn(`[${this.getButtonData.name}] Failed to make a button:`, { btnData });
		}

		const { uri, route = '' } = btnData;
		// href will be either a uri or a route (and a route to the main page if neither exists)
		const href = uri || `#${route}`;

		return {
			...btnData,
			href,
			[ATTRIBUTES.text]:
				btnData[ATTRIBUTES.text] ||
				[
					btnData[ATTRIBUTES.name],
					btnData[ATTRIBUTES.title],
					btnData[ATTRIBUTES.subtitle],
				].filter((line) => line !== undefined),
		};
	}

	// TODO: Make sure this gets called properly when it needs to be
	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		gsap.killTweensOf(this.el);
		this.undelegateEvents();
		this.remove();
	}
}

/**
 * Creates and returns Generic buttons.
 *
 * @param {GenericButtonOptions | GenericButtonOptions[] | string[]} btnData Array or Object filled with arbitrary button data
 * @returns {Element[]} Instance(s) to easily add to DOM
 */
export function createButtons(btnData) {
	getLogger(GenericButton.name).log(`[${createButtons.name}]`, { btnData });

	if (!Array.isArray(btnData)) {
		btnData = [btnData];
	}

	return btnData.map((data) => {
		if (typeof data === 'string') {
			const pageBtnData = /** @type {GenericButtonOptions} */ (
				Arcade.instance?.getPage(data)
			);
			return new GenericButton(pageBtnData).el;
		}

		return new GenericButton(data).el;
	});
}
